import {
  BayLevelEnum,
  IOpenVesselDefinitionV1,
  PortStarboardEnum,
  TContainerLengths,
} from "open-vessel-definition";
import { EnumHelpers, pad3 } from "@tedivo/tedivo-pure-helpers";
import { IAllBaysWith40sArePairedOutput, IValidationOutput } from "@tedivo/tedivo-ovd-validators";
import { IExportStafOptions, createStafFile } from "./createStafFile";
import { ISlOption, TedivoForm, translateTedivoForm } from "@tedivo/tedivo-form";
import {
  isNotFalse,
  isTrue,
  pickAllowed,
  readSavedExportOptions,
  saveExportOptions,
} from "./exportOptionsMemory";

import { ICGsStatistics } from "@tedivo/tedivo-bay-grid-pure";
import { IJoinedRowTierPattern } from "open-vessel-definition/build/src/models/base/types/IPositionPatterns";
import { SlSwitch } from "@shoelace-style/shoelace";
import { TAddRemoveCgs } from "./cgsExportOptions";
import UoLcgReferenceEnum from "../../../../../../../app/enums/UoLcgReferenceEnum";
import VcgVerticalReferenceEnum from "../../../../../../../app/enums/VcgVerticalReferenceEnum";
import { createCgsOptions } from "./cgsExportOptions";
import { createSimpleAlert } from "@tedivo/tedivo-ui";
import { getTranslation } from "../../../../../../../app/i18/i18tn";
import globalUnits from "../../../../../../../app/units/globalUnits";
import ovdJsonStore from "../../../../../../../app/stores/OVDJsonStore";
import { z } from "zod";

export type TDownloadType = "ovdJson" | "stafFile" | "nsdFile" | "cloneToMyAccount" | "compare";

export async function createStafFileBlobWithDialog(
  json: IOpenVesselDefinitionV1 | undefined,
  cgsStats: ICGsStatistics | undefined,
): Promise<Blob | undefined> {
  return new Promise((resolve) => {
    if (json === undefined) {
      resolve(undefined);
      return;
    }

    const isValidPairedBays = ovdJsonStore.ovdValidator
      .getLastResult(["exportStaf"])
      .find((v) => v.name === "allBaysWith40sArePairedValidator") as
      IValidationOutput<IAllBaysWith40sArePairedOutput> | undefined;

    // 0. Check validation results about paired bays necessary for STAF export
    if (isValidPairedBays && !isValidPairedBays.isValid) {
      const listOfBays = (isValidPairedBays.result.invalidResults || [])
        .map(
          ({ bay, level }) =>
            `${bay} ${getTranslation(`enums:BayLevelEnum.${BayLevelEnum[level]}`)}`,
        )
        .join(", ");

      // 0.1 Show an alert with the list of bays that are not paired
      createSimpleAlert({
        title: getTranslation("view:download.downloadSTAF"),
        message:
          getTranslation(`errors:validators.${isValidPairedBays.name}`) +
          "<br /><br />" +
          listOfBays,
        onOk: () => null,
        closeLabel: getTranslation("general:common.ok"),
      });

      resolve(undefined);
      return;
    }

    const canAddArtificialBottomBases = !!ovdJsonStore.lcgVcgTcgAndPairings?.missingImportantVcgs;

    const canAddArtificialLcgs = !!ovdJsonStore.lcgVcgTcgAndPairings?.missingImportantLcgs;

    const canAddArtificialCgs = canAddArtificialBottomBases || canAddArtificialLcgs;

    // Guard moved here from createStafExportDialog so the outer Promise always
    // resolves instead of hanging permanently when required data is absent.
    if (!ovdJsonStore.currentJson || !cgsStats) {
      resolve(undefined);
      return;
    }

    // 1. Open the dialog to configure the STAF export
    createStafExportDialog({
      json,
      canAddArtificialCgs,
      // Resolve the outer Promise when the user cancels the dialog so callers
      // don't hang waiting for a result that will never come.
      onCancel: () => resolve(undefined),
      onExport: (stafOptions) => {
        //let stafFile: string | undefined = undefined;
        let jsonToUse: IOpenVesselDefinitionV1 = json;

        if (
          ovdJsonStore.lcgVcgTcgAndPairings &&
          canAddArtificialCgs &&
          stafOptions.addRemoveCgs === "addArtificial"
        ) {
          // If the user wants to add artificial CGs, we need to add
          // artificial CGs to the JSON before exporting
          const jsonWithArtificialCgs = ovdJsonStore.lcgVcgTcgAndPairings.addArtificialCgsToJson(
            json,
            ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases,
          );

          jsonToUse = jsonWithArtificialCgs;
        }

        if (!stafOptions.tierRemap) stafOptions.tier82is = 82;

        const { stafText: stafFile, slotsAbove100 } = createStafFile(jsonToUse, stafOptions);

        if (stafFile) {
          if (slotsAbove100.length) {
            const content = document.createElement("div");
            const messageT = document.createElement("p");
            const messageS = document.createElement("div");
            messageS.style.fontFamily = "monospace";
            const dc = document.createDocumentFragment();

            messageT.innerHTML = getTranslation("general:exportToStaf.slotsIncomplete");
            const maxSlotsAbove100 = slotsAbove100.length;
            slotsAbove100.forEach((s, idx) => {
              const bay = s.substring(0, 2);
              const row = s.substring(2, 4);
              const tier = s.substring(4);
              const span = document.createElement("span");
              span.innerHTML = `${pad3(Number(bay))}${row}<strong>${tier}</strong>${idx !== maxSlotsAbove100 - 1 ? ", " : ""}`;
              dc.appendChild(span);
            });

            messageS.appendChild(dc);

            content.appendChild(messageT);
            content.appendChild(messageS);

            createSimpleAlert({
              title: getTranslation("general:exportToStaf.slotTiersHigherThan98"),
              message: content,
              closeLabel: getTranslation("general:common.ok"),
            });
          }

          resolve(
            new Blob([stafFile], {
              type: "text/plain",
            }),
          );
          return;
        } else {
          resolve(undefined);
          return;
        }
      },
    });
  });
}

/** Preferences key holding the remembered options, mirroring `nsdExport-options`. */
const STAF_EXPORT_PREF_ID = "stafExport-options";

/**
 * What is remembered between exports: the user's own choices, plus whether the
 * Advanced options panel was left expanded.
 *
 * Deliberately excluded:
 * - `lcgRef` / `tcgRef` / `vcgRef` come from `globalUnits`, which already
 *   persists them through the Options dialog. A second copy here would quietly
 *   override that global setting on every export.
 * - `removeBaysWithNonSizeSlots` is computed from the file being exported.
 * - `tier82is` is derived from the file's tiers and travels as hiddenData.
 */
interface IStafExportPrefs {
  addRemoveCgs: TAddRemoveCgs;
  remove100s: boolean;
  tierRemap: boolean;
  remove24s: boolean;
  advancedOpen: boolean;
}

const STAF_EXPORT_DEFAULTS: IStafExportPrefs = {
  addRemoveCgs: "none",
  remove100s: true,
  tierRemap: true,
  remove24s: true,
  advancedOpen: false,
};

/**
 * The options as the user last exported them.
 *
 * Nothing stored is trusted - a saved `addRemoveCgs` can be inapplicable to this
 * file ("addArtificial" is not rendered when there are no missing CGs), so it is
 * checked against the very option list the dialog builds.
 *
 * The three booleans default to ON, so only an explicitly stored `false` turns
 * one off and a missing key keeps the original behaviour.
 *
 * @param cgsOptions the CGs options available for the current file.
 */
export function readSavedStafExportOptions(cgsOptions: ISlOption[]): IStafExportPrefs {
  const saved = readSavedExportOptions<IStafExportPrefs>(STAF_EXPORT_PREF_ID);

  return {
    addRemoveCgs: pickAllowed(
      saved.addRemoveCgs,
      cgsOptions.map((o) => o.value) as TAddRemoveCgs[],
      STAF_EXPORT_DEFAULTS.addRemoveCgs,
    ),
    remove100s: isNotFalse(saved.remove100s),
    tierRemap: isNotFalse(saved.tierRemap),
    remove24s: isNotFalse(saved.remove24s),
    advancedOpen: isTrue(saved.advancedOpen),
  };
}

/** Remembers the options for the next export. */
export function saveStafExportOptions(prefs: IStafExportPrefs): void {
  saveExportOptions<IStafExportPrefs>(STAF_EXPORT_PREF_ID, prefs);
}

const FormValidator: z.Schema<IExportStafOptions> = z.object({
  tier82is: z.custom<number>().transform((value) => Number(value)),
  tierRemap: z.boolean(),
  lcgRef: z.number(),
  tcgRef: z.number(),
  vcgRef: z.number(),
  removeBaysWithNonSizeSlots: z.boolean(),
  addRemoveCgs: z.enum(["none", "remove", "addArtificial"]),
  remove100s: z.boolean(),
});

/**
 * Opens a modal dialog to configure and export a STAF file based on the provided vessel definition and CG statistics.
 *
 * This function dynamically creates a dialog with various export options, including LCG/TCG/VCG references,
 * tier remapping, and advanced options such as removing certain bays or CGs. The dialog is built using custom
 * web components and a form generator, and resolves with the generated STAF file string or `null` if the dialog
 * is cancelled.
 *
 * @param json - The vessel definition JSON object conforming to `IOpenVesselDefinitionV1`.
 * @param cgsStats - The CG statistics object, or `undefined` if not available.
 * @returns A promise that resolves to the exported STAF file as a string, or `null` if the export was cancelled.
 *
 * @remarks
 * - The dialog uses translations for labels and options.
 * - Advanced options are hidden in a collapsible section.
 * - The function checks for required data and disables or hides options accordingly.
 * - The dialog is removed from the DOM after completion or cancellation.
 */
function createStafExportDialog({
  json,
  canAddArtificialCgs,
  onCancel,
  onExport,
}: {
  json: IOpenVesselDefinitionV1;
  canAddArtificialCgs: boolean;
  onCancel: () => void;
  onExport: (exportStafOptions: IExportStafOptions) => void;
}) {
  const hiddenData: Partial<IExportStafOptions> = { tierRemap: false };

  const dialog = document.createElement("sl-dialog");
  dialog.label = getTranslation("view:download.downloadSTAFOptions");
  // dialog.style.setProperty("--width", "900px");

  const btnGo = document.createElement("sl-button");
  btnGo.innerHTML = getTranslation("general:common.export");
  btnGo.slot = "footer";
  btnGo.variant = "primary";
  btnGo.type = "submit";

  const lcgRefOptions = EnumHelpers.getNamesAndValues(UoLcgReferenceEnum).map(
    ({ value, name }) => ({
      value,
      label: getTranslation(`enums:UoLcgReferenceEnum.${name}`),
    }),
  );

  const tcgRefOptions = EnumHelpers.getNamesAndValues(PortStarboardEnum).map(({ value, name }) => ({
    value,
    label: getTranslation(`enums:PortStarboardEnum.${name}`),
  }));

  const vcgRefOptions = EnumHelpers.getNamesAndValues(VcgVerticalReferenceEnum).map(
    ({ value, name }) => ({
      value,
      label: getTranslation(`enums:VcgVerticalReferenceEnum.${name}`),
    }),
  );

  const lpp = json.shipData?.lcgOptions?.lpp || 0;

  // const cgsPartData = {
  //   lcgValues: json.shipData?.lcgOptions?.values || ValuesSourceEnum.ESTIMATED,
  //   lcgDataFilled: Math.floor(
  //     (cgsStats.definedLcgs / (cgsStats.countLcgs || 1)) * 100,
  //   ),
  // };

  const minAboveTier = json.sizeSummary.minAboveTier;
  const isAboveBelow82 = minAboveTier !== undefined && minAboveTier < 82;

  // const optionsTiersReamp: { value: number; label: string }[] = [];
  const has24s = json.sizeSummary.maxBelowTier !== undefined && json.sizeSummary.maxBelowTier > 22;

  const cgsOptions = createCgsOptions(canAddArtificialCgs);
  const saved = readSavedStafExportOptions(cgsOptions);

  let slToggle24s: SlSwitch | undefined = undefined;
  if (has24s) {
    slToggle24s = document.createElement("sl-switch");
    slToggle24s.innerHTML = getTranslation("general:exportToStaf.remove24s");
    slToggle24s.helpText = getTranslation("general:exportToStaf.remove24sHelp");
    slToggle24s.checked = saved.remove24s;
    slToggle24s.className = "space-below";
  }

  if (!isAboveBelow82) {
    hiddenData.tier82is = 82;
  } else {
    hiddenData.tier82is = 82 - minAboveTier + 82;
  }

  const tedivoForm = new TedivoForm<IExportStafOptions>({
    fields: [
      {
        name: "addRemoveCgs",
        type: "select",
        label: getTranslation("general:exportToStaf.cgsOptions"),
        options: cgsOptions,
        initialValue: saved.addRemoveCgs,
      },
      {
        name: "lcgRef",
        type: "radioButtonList",
        label: getTranslation("view:lcgReference"),
        fieldset: true,
        isNumericEnum: true,
        options: lcgRefOptions,
        initialValue: globalUnits.units.lcgRef,
        disabled: lpp === 0,
      },
      {
        name: "tcgRef",
        type: "radioButtonList",
        label: getTranslation("view:tcgDirection"),
        fieldset: true,
        isNumericEnum: true,
        options: tcgRefOptions,
        initialValue: globalUnits.units.tcgDir,
      },
      {
        name: "vcgRef",
        type: "radioButtonList",
        label: getTranslation("view:vcgReference"),
        fieldset: true,
        isNumericEnum: true,
        options: vcgRefOptions,
        initialValue: globalUnits.units.vcgRef,
      },
      // isAboveBelow80
      //   ? {
      //       name: "tier82is",
      //       type: "select",
      //       options: optionsTiersReamp,
      //       label: "general:exportToStaf.remapAboveTiers",
      //       initialValue: 82,
      //     }
      //   : undefined,
      isAboveBelow82
        ? {
            name: "tierRemap",
            type: "checkbox",
            label: "general:exportToStaf.remapAboveTiers",
            initialValue: saved.tierRemap,
          }
        : undefined,
      {
        name: "remove100s",
        type: "checkbox",
        label: getTranslation("general:exportToStaf.removeTier100SlotExceptions"),
        initialValue: saved.remove100s,
      },
      {
        name: "removeBaysWithNonSizeSlots",
        type: "checkbox",
        label: getTranslation("view:download.removeBaysWithNonSizeSlots"),
        initialValue: areThereSlotsWithoutSizeInSomeBay(),
      },
    ],
    onSubmit: () => null,
    hiddenData,
    formValidator: FormValidator,
    submitButton: btnGo,
    logValidationResult: true,
  });

  translateTedivoForm<IExportStafOptions>({ tedivoForm, getTranslation });

  const slDetails = document.createElement("sl-details");
  slDetails.summary = getTranslation("general:common.advancedOptions");
  slDetails.open = saved.advancedOpen;

  const slIconExpand = document.createElement("sl-icon");
  slIconExpand.name = "chevron-down";
  slIconExpand.slot = "expand-icon";
  const slIconCollapse = document.createElement("sl-icon");
  slIconCollapse.name = "chevron-left";
  slIconCollapse.slot = "collapse-icon";

  slDetails.appendChild(slIconExpand);
  slDetails.appendChild(slIconCollapse);
  slDetails.appendChild(tedivoForm.form);

  // Add at the top the toggles that are outside the Advanced Options
  if (slToggle24s) dialog.appendChild(slToggle24s);

  dialog.appendChild(slDetails);
  dialog.appendChild(btnGo);

  dialog.addEventListener("sl-request-close", () => {
    // Resolve the outer Promise before removing the dialog so callers don't
    // hang when the user closes via Escape or clicking outside.
    onCancel();
    dialog.remove();
  });

  btnGo.addEventListener("click", () => {
    const validResult = tedivoForm.doSubmitForm();

    if (validResult.success) {
      const values = validResult.data;
      values.remove24s = slToggle24s?.checked;

      // Read the controls before the dialog goes away. tierRemap and remove24s
      // are only rendered for files that need them - and an absent tierRemap
      // still arrives as the hiddenData `false` - so the remembered choice is
      // kept whenever the control was not on screen: exporting a vessel without
      // 24s must not wipe the preference for one that has them.
      saveStafExportOptions({
        addRemoveCgs: values.addRemoveCgs,
        remove100s: values.remove100s ?? saved.remove100s,
        tierRemap: isAboveBelow82 ? values.tierRemap : saved.tierRemap,
        remove24s: slToggle24s ? slToggle24s.checked : saved.remove24s,
        advancedOpen: slDetails.open,
      });

      dialog.hide();
      dialog.remove();

      onExport(values);
    }
  });

  document.body.appendChild(dialog);
  dialog.show();

  function areThereSlotsWithoutSizeInSomeBay() {
    const bls = json.baysData;
    for (let i = 0; i < bls.length; i++) {
      const bl = bls[i];

      const perSlotInfo = bl.perSlotInfo;
      if (!perSlotInfo) continue;

      const slotsData = Object.keys(perSlotInfo) as IJoinedRowTierPattern[];
      if (slotsData.length === 0) continue;

      const allSizesSet = new Set<TContainerLengths>();

      // Get all sizes in this bay
      slotsData.forEach((slot) => {
        const slotSizes = Object.keys(perSlotInfo[slot].sizes).map(Number) as TContainerLengths[];

        slotSizes.forEach((size) => allSizesSet.add(size));
      });

      const allSizes = Array.from(allSizesSet);

      if (allSizes.length === 0) return true;
    }

    return false;
  }
}
